import { action, computed, observable, runInAction } from 'mobx'
import {
  IUpdateCheckRPC,
  IUpdateDownloadRPC,
  IUpdateDownloadCancelRPC,
  IUpdateInstallRPC,
} from '@prisma/studio-types'

import ConfigStore from 'stores/config/ConfigStore'

class UpdateStore {
  @observable hasCheckedForUpdates: boolean = false
  @observable latestVersion: string = STUDIO_VERSION
  @observable changelog: string = ''
  @observable isDownloading: boolean = false
  @observable isUpdateReady: boolean = false
  @observable isInstalling: boolean = false

  @computed
  get isUpToDate() {
    return this.latestVersion === STUDIO_VERSION
  }

  @action
  check = async () => {
    if (STUDIO_ENV_DEVELOPMENT || !ConfigStore.updates) {
      return
    }

    runInAction(() => {
      this.hasCheckedForUpdates = false
      this.isDownloading = false
      this.isUpdateReady = false
      this.isInstalling = false
    })

    const { error, data } = await window.transport.request<IUpdateCheckRPC>({
      channel: 'update',
      action: 'check',
      payload: {
        data: null,
      },
    })

    if (error) {
      console.error('Update check failed', error)
      return
    }

    const { available, version, changelog } = data

    if (available) {
      runInAction(() => {
        this.hasCheckedForUpdates = true
        this.latestVersion = version!
        this.changelog = changelog!
      })
    } else {
      runInAction(() => {
        this.hasCheckedForUpdates = true
        this.latestVersion = STUDIO_VERSION
      })
    }
  }

  @action
  download = async () => {
    if (STUDIO_ENV_DEVELOPMENT || !ConfigStore.updates) {
      return Promise.resolve()
    }

    if (this.isDownloading) {
      // If a download is already going on, there's no need to start another one
      return Promise.reject('Another download is in progress')
    }

    runInAction(() => {
      this.isDownloading = true
      this.isUpdateReady = false
      this.isInstalling = false
    })

    await window.transport.request<IUpdateDownloadRPC>({
      channel: 'update',
      action: 'download',
      payload: {
        data: null,
      },
    })

    runInAction(() => {
      this.isDownloading = false
      this.isUpdateReady = true
    })
  }

  @action
  cancelDownload = async () => {
    if (STUDIO_ENV_DEVELOPMENT || !ConfigStore.updates) {
      return
    }

    if (!this.isDownloading) {
      // If a download is not in progress, there's nothing to do
      return Promise.resolve()
    }

    await window.transport.request<IUpdateDownloadCancelRPC>({
      channel: 'update',
      action: 'downloadCancel',
      payload: {
        data: null,
      },
    })

    runInAction(() => {
      this.isDownloading = false
      this.isUpdateReady = false
    })
  }

  install = async () => {
    if (STUDIO_ENV_DEVELOPMENT || !ConfigStore.updates) {
      return
    }

    runInAction(() => {
      this.isDownloading = false
      this.isUpdateReady = true
      this.isInstalling = true
    })

    await window.transport.request<IUpdateInstallRPC>({
      channel: 'update',
      action: 'install',
      payload: {
        data: null,
      },
    })

    runInAction(() => {
      this.isUpdateReady = false
      this.isInstalling = false
    })
  }
}

export default new UpdateStore()
